

import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  PencilSquareIcon,
  EyeIcon,
  ArrowPathIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import {
  fetchOrders,
  updateOrders,
  getOrdersByCompanyId,
} from "../../features/admin/OrderSlice";
import TableComponent from "../../components/TableComponent";
import { toast } from "react-toastify";
import OrderModal from "../../components/admin/OrderModal";
import { getCompanyByUserId } from "../../features/admin/CompanySlice";
import ConfirmModal from "../../components/ConfirmModal.js";
import AuditHistoryButton from "../../components/admin/AuditHistoryButton";

const getUserPermissions = () => {
  const permissions = localStorage.getItem("permissions");
  return permissions
    ? JSON.parse(permissions).map((p) => p.permissionSlug)
    : [];
};

const userId = localStorage.getItem("userId");
const hasPermission = (requiredPermission) => {
  if (userId == 1) {
    return true;
  }
  const userPermissions = getUserPermissions();
  return userPermissions.includes(requiredPermission);
};

const Orders = () => {
  const dispatch = useDispatch();
  const { orders, status, error } = useSelector((state) => state.orders);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOpenConfirm, setIsOpenConfirm] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [acceptId, setAcceptId] = useState(null);

  const handleOpenModal = (item = null) => {
    setCurrentOrder(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentOrder(null);
  };

  const handleOpenConfirm = (e, id) => {
    e.preventDefault();
    setIsOpenConfirm(true);
    setAcceptId(id);
  };

  const handleCloseConfirm = () => {
    setIsOpenConfirm(false);
    setAcceptId(null);
  };

  const handleAcceptConfirm = () => {
    handleUpdateStatus(acceptId);
    handleCloseConfirm();
  };

  const userId = localStorage.getItem("userId");
  useEffect(() => {
    getCompanyInfo();
  }, []);

  const getCompanyInfo = async () => {
    const companyData = await dispatch(getCompanyByUserId(userId)).unwrap();
    setCompanyInfo(companyData);
    // if (companyData) {
    await getOrderData(companyData?.id);
    // }
  };

  const getOrderData = async (id) => {
    if (hasPermission("BILL_SHOW")) {
      await dispatch(fetchOrders()).unwrap();
      return;
    }
    await dispatch(getOrdersByCompanyId(id)).unwrap();
  };

  const handleUpdateStatus = async (id) => {
    try {
      await dispatch(
        updateOrders({ id: id, newPost: { status: "success" } })
      ).unwrap();
      await getOrderData(companyInfo?.id);
      toast.success("Duyệt giao dịch thành công.");
    } catch (error) {
      toast.error("Đã xảy ra lỗi! Vui lòng thử lại.");
      console.error("Lỗi khi lưu sản phẩm:", error);
    }
  };

  const headers = [
    {
      key: "id",
      label: "Mã giao dịch",
    },
    {
      key: "fullName",
      label: "Người đặt hàng",
      render: (item) => (
        <div>
          <p className="font-medium">{item.fullName}</p>
        </div>
      ),
    },
    {
      key: "phone",
      label: "Số điện thoại",
    },
    {
      key: "province",
      label: "Tỉnh/ Thành phố",
    },
    {
      key: "district",
      label: "Quận/ Huyện",
    },
    {
      key: "status",
      label: "Trạng thái",
      render: (item) => (
        <span
          className={`px-3 py-1 rounded-full text-sm ${
            item.status === "success"
              ? "bg-green-100 text-green-800"
              : item.status === "pending"
              ? "bg-yellow-100 text-yellow-800"
              : "bg-red-100 text-red-800"
          } whitespace-nowrap`}
        >
          {item.status === "pending"
            ? "Chờ xác nhận"
            : item.status === "success"
            ? "Đã xác nhận"
            : "Đã huỷ"}
        </span>
      ),
    },
    { key: "setting", label: "Thao tác" },
  ];

  const tableSettings = {
    renderSettings: (item) => (
      <div className="flex space-x-2">
        {hasPermission("BILL_UPDATE") && (
          <button
            disabled={item.status == "success"}
            // onClick={(e) => handleUpdateStatus(e, item.id)}
            onClick={(e) => handleOpenConfirm(e, item.id)}
            title="Duyệt"
            className="btn btn-outline btn-warning btn-sm px-3 my-1 rounded"
          >
            <CheckIcon className="w-5 h-5" />
          </button>
        )}
        <button
          onClick={() => handleOpenModal(item)}
          title="Xem chi tiết"
          className="btn btn-outline btn-info btn-sm px-3 my-1 rounded"
        >
          <EyeIcon className="w-5 h-5" />
        </button>
        <AuditHistoryButton module="ORDER" entityType="Order" entityId={item.id} />
      </div>
    ),
  };

  if (status === "loading") {
    return (
      <div className="flex justify-center items-center h-64">
        <ArrowPathIcon className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (status === "failed") {
    return (
      <div className="p-4 text-red-500 bg-red-50 rounded-md">
        Đã xảy ra lỗi: {error}
      </div>
    );
  }

  const filterData = orders.filter((item) =>
    searchValue === ""
      ? true
      : [item.fullName, item.phone].some((field) =>
          field?.toLowerCase().includes(searchValue.toLowerCase())
        )
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold ">Quản lý giao dịch</h1>
        <div className=" flex items-center space-x-2">
          <label
            htmlFor="userSearchFilter"
            className="text-sm font-medium w-24"
          >
            Tìm kiếm:
          </label>
          <input
            type="search"
            className="w-full px-2 py-1 border rounded-lg focus:outline-none pr-10"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </div>
      </div>
      <div className="rounded-lg shadow">
        <TableComponent
          headers={headers}
          data={filterData}
          settings={tableSettings}
        />
      </div>

      {isModalOpen && (
        <OrderModal
          orderId={currentOrder?.id || null}
          onClose={handleCloseModal}
        />
      )}

      <ConfirmModal
        isOpen={isOpenConfirm}
        onClose={handleCloseConfirm}
        onSubmit={handleAcceptConfirm}
        message={"Bạn có muốn duyệt đơn hàng này không?"}
      />
    </div>
  );
};

export default Orders;



